<template>
    <div class="d-flex flex-column gap-4">
        <CalendarEntry
            ref="calendarEntry"
            v-for="(entry, index) in entries"
            :key="entry.id"
            :id="entry.id"
            :system-key="entry.systemKey"
            :can-delete="index > 0 && allowedToRemove"
            :max-validity-start="maxValidityStart"
            :max-validity-end="maxValidityEnd"
            :preselected-user-data="entry.userData"
            @delete="deleteEntry"
            @user-data-updated="userDataUpdated"
        />
        <div v-if="canAddEntry">
            <button
                @click.prevent="addEntry()"
                class="btn btn-link p-0 text-primary"
                v-html="$t('configurator.add_time_period')"
            ></button>
        </div>
        <div v-if="formErrors.length > 0" class="alert alert-danger">
            <span class="d-block" v-for="error in formErrors" :key="error">
                {{ error }}
            </span>
        </div>
        <button
            @click.prevent="submit"
            class="btn btn-primary mb-mobile-1"
            v-html="$t('configurator.determine_vignettes')"
        ></button>

        <div v-if="resultReady" ref="result" class="row">
            <ProductSummary
                :selected-products="configurationProducts"
                :voucher="null"
                :url="productUrl"
                source="configurator"
            ></ProductSummary>
        </div>
        <div v-else-if="requestError">
            <div class="invalid-feedback d-block error-font-size">{{ $t('generic.failed') }}</div>
        </div>
    </div>
</template>

<script>
import CalendarEntry from './CalendarEntry.vue';
import ProductSummary from '../../Europamaut/ProductSummary.vue';

export default {
    name: 'Calendar',

    components: {
        CalendarEntry,
        ProductSummary,
    },

    inject: ['systems'],

    props: {
        selectSystems: {
            type: Array,
            default: () => [],
        },
        allowedToAdd: {
            type: Boolean,
            default: true,
        },
        allowedToRemove: {
            type: Boolean,
            default: true,
        },
        maxValidityStart: {
            type: String,
            required: false,
        },
        maxValidityEnd: {
            type: String,
            required: false,
        },
    },

    watch: {
        selectSystems() {
            this.entries = [];
            this.setSelectedSystems();
        },
    },

    data() {
        return {
            maxEntries: 30,
            formErrors: [],
            entries: [],
            requestError: false,
            requestOngoing: false,
            configuration: [],
            productUrl: null,
            controller: null,
            lastEntryId: null,
        };
    },

    computed: {
        canAddEntry() {
            return this.allowedToAdd && this.entries.length < this.maxEntries;
        },
        resultReady() {
            return !this.requestOngoing && !this.requestError && this.configuration?.length > 0;
        },
        configurationProducts() {
            if (this.configuration.length <= 0) {
                return {};
            }

            const products = [];
            this.configuration.forEach((v) => {
                products.push({
                    listingId: 'configurator' + Math.random().toString(36),
                    option: v.option,
                    product: v.product,
                });
            });

            return products;
        },
    },

    methods: {
        makeId() {
            return (Math.random() + 1).toString(36).substring(6);
        },
        addEntry(systemKey = null) {
            if (!this.canAddEntry && this.selectSystems.length <= 0) return;

            const userData = {};
            if (this.lastEntryId) {
                const lastEntry = this.getEntry(this.lastEntryId);
                userData.startDate = lastEntry?.userData?.endDate;
            }

            const id = this.makeId();
            this.lastEntryId = id;
            this.entries.push({
                id: id,
                systemKey,
                userData: userData,
            });
        },
        deleteEntry(id) {
            if (this.requestOngoing) {
                return;
            }

            this.entries = this.entries.filter((entry) => entry.id !== id);
            this.lastEntryId = this.entries.at(-1)?.id;

            if (this.configuration.length > 0) {
                this.submit();
            }
        },
        getEntry(id) {
            return this.entries.find((e) => e.id === id);
        },
        userDataUpdated(id, data) {
            const idx = this.entries.findIndex((entry) => entry.id === id);
            if (idx > -1) {
                this.entries[idx] = { ...this.entries[idx], userData: data };
            }
            this.resetResult();
        },
        resetResult() {
            if (this.controller) {
                this.controller.abort();
            }
            this.configuration = [];
            this.requestError = false;
            this.requestOngoing = false;
        },
        getSystem(systemKey) {
            return this.systems.find((system) => system.systemKey === systemKey);
        },
        validate() {
            let hasErrors = false;
            this.$set(this, 'formErrors', []);

            for (const ref of this.$refs.calendarEntry) {
                if (!ref.validate()) {
                    hasErrors = true;
                }
            }

            // Validate same countries have the same selected vehicle type.
            const countryVehicleTypes = new Map();
            for (const userData of this.getUserData()) {
                if (!countryVehicleTypes.has(userData.systemKey)) {
                    countryVehicleTypes.set(userData.systemKey, userData.vehicleType);
                } else if (countryVehicleTypes.get(userData.systemKey) !== userData.vehicleType) {
                    const system = this.getSystem(userData.systemKey);
                    this.formErrors.push(
                        this.$t('configurator.validation_country_vehicle_type_must_be_equal', {
                            country: system.localizedName,
                        })
                    );
                    hasErrors = true;
                }
            }

            return !hasErrors;
        },
        setSelectedSystems() {
            for (const system of this.selectSystems) {
                this.addEntry(system);
            }

            if (this.entries.length === 0) {
                this.addEntry();
            }
        },
        submit() {
            if (!this.validate() || this.requestOngoing) {
                return;
            }

            this.requestOngoing = true;
            this.requestError = false;
            this.configuration = [];

            this.controller = new AbortController();

            axios
                .get('/' + this.$language + '/backend/calculate/products', {
                    signal: this.controller.signal,
                    params: {
                        data: this.getUserData(),
                    },
                })
                .then((res) => {
                    if (!res.data.success) {
                        this.requestError = true;
                        return;
                    }

                    this.configuration = res.data.products;
                    this.productUrl = res.data.combinedVignetteUrl;
                })
                .catch((err) => {
                    if (axios.isCancel(err)) {
                        return;
                    }
                    this.requestError = true;
                })
                .finally(() => {
                    this.requestOngoing = false;
                    this.controller = null;

                    this.$nextTick(() => {
                        if (this.$refs.result) {
                            this.$refs.result.scrollIntoView({ block: 'start', behavior: 'smooth' });
                        }
                    });
                });
        },
        getUserData() {
            return this.entries.map((entry) => entry.userData);
        },
    },

    mounted() {
        this.setSelectedSystems();
    },
};
</script>

<style lang="scss">
html {
    scroll-padding-top: 135px;
    @media (max-width: 1024px) {
        scroll-padding-top: 120px;
    }
}
</style>

<style scoped lang="scss">
.gap-4 {
    gap: 1rem;
}
.error-font-size {
    font-size: 16px;
}
</style>
